import React from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/authStore";
import { psychologistTestsApi } from "../api/psychologistTestsApi";
import { useCreateSectionGate } from "../subscription/useCreateSectionGate";
import { useToast } from "../ui/toast/ToastProvider";

const EMPTY_QUESTION = { text: "", type: "SCALE", required: true, options: "" };

function trim(value, max = 2000) {
  return String(value || "").trim().slice(0, max);
}

function normalizeQuestion(question, index) {
  const type = String(question.type || "SCALE").toUpperCase();
  const options = String(question.options || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

  return {
    position: index + 1,
    text: trim(question.text, 500),
    type: ["SCALE", "SINGLE", "MULTIPLE", "TEXT"].includes(type) ? type : "SCALE",
    required: question.required !== false,
    options: type === "TEXT" || type === "SCALE" ? [] : options.slice(0, 12),
  };
}

export default function PsychologistTestBuilder() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { ensureWithinLimit, checkCreateAccess, limitModal } = useCreateSectionGate();
  const canManage = role === "PSYCHOLOGIST" || role === "ADMIN";
  const [entryCheckDone, setEntryCheckDone] = React.useState(false);
  const [entryAllowed, setEntryAllowed] = React.useState(true);

  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({
    title: "",
    topic: "",
    description: "",
    estimatedMinutes: "10",
    scope: "PRIVATE",
    questions: [{ ...EMPTY_QUESTION }],
  });

  React.useEffect(() => {
    let alive = true;
    if (!canManage) {
      setEntryAllowed(false);
      setEntryCheckDone(true);
      return undefined;
    }

    (async () => {
      const allowed = await checkCreateAccess("/dashboard/tests/new");
      if (!alive) return;
      setEntryAllowed(Boolean(allowed));
      setEntryCheckDone(true);
    })();

    return () => {
      alive = false;
    };
  }, [canManage, checkCreateAccess]);

  const updateField = (name) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const updateQuestion = (index, patch) => {
    setForm((current) => {
      const questions = [...current.questions];
      questions[index] = { ...questions[index], ...patch };
      return { ...current, questions };
    });
  };

  const addQuestion = () => {
    setForm((current) => ({ ...current, questions: [...current.questions, { ...EMPTY_QUESTION }] }));
  };

  const removeQuestion = (index) => {
    setForm((current) => {
      const questions = current.questions.filter((_, idx) => idx !== index);
      return { ...current, questions: questions.length ? questions : [{ ...EMPTY_QUESTION }] };
    });
  };

  const buildPayload = (publish = false) => {
    const questions = form.questions.map(normalizeQuestion).filter((item) => item.text);
    return {
      title: trim(form.title, 160),
      topic: trim(form.topic, 120),
      description: trim(form.description, 2000),
      estimatedMinutes: Math.max(1, Math.min(240, Number(form.estimatedMinutes) || 10)),
      scope: String(form.scope || "PRIVATE").toUpperCase(),
      status: publish ? "PUBLISHED" : "DRAFT",
      questions,
    };
  };

  const onSave = async (publish = false) => {
    const payload = buildPayload(publish);
    if (!payload.title) {
      toast.error("Укажите название теста");
      return;
    }
    if (!payload.questions.length) {
      toast.error("Добавьте хотя бы один вопрос");
      return;
    }

    try {
      const list = await psychologistTestsApi.listMine({ page: 0, size: 1 });
      const usage = Number(list?.totalElements) || 0;
      const allowed = ensureWithinLimit({ featureKey: "tests", usage });
      if (!allowed) return;
    } catch (limitError) {
      toast.error(limitError?.message || "Не удалось проверить лимит подписки");
      return;
    }

    setSaving(true);
    try {
      const created = await psychologistTestsApi.create(payload);
      toast.success(publish ? "Тест создан и опубликован" : "Черновик теста сохранён");
      if (created?.id && publish && psychologistTestsApi.publish) {
        try {
          await psychologistTestsApi.publish(created.id);
        } catch {}
      }
      navigate("/dashboard/tests");
    } catch (err) {
      toast.error(err?.message || "Не удалось сохранить тест");
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <section className="dashboard-placeholder" style={{ marginBottom: 24 }}>
        <div className="dashboard-placeholder__hero">
          <h3>Доступ ограничен</h3>
          <p>Создание тестов доступно только психологам и администраторам.</p>
        </div>
        <div className="dashboard-placeholder__actions">
          <Link className="dashboard__btn dashboard__btn--ghost" to="/dashboard">
            ← Вернуться в кабинет
          </Link>
        </div>
      </section>
    );
  }

  if (!entryCheckDone) return <section className="psy-test-builder" />;

  if (!entryAllowed) {
    return (
      <>
        <section className="dashboard-placeholder" style={{ marginBottom: 24 }}>
          <div className="dashboard-placeholder__hero">
            <h3>Лимит достигнут</h3>
            <p>Для создания новых тестов перейдите на тариф выше в подписке.</p>
          </div>
          <div className="dashboard-placeholder__actions">
            <Link className="dashboard__btn dashboard__btn--ghost" to="/dashboard/tests">
              ← К списку тестов
            </Link>
          </div>
        </section>
        {limitModal}
      </>
    );
  }

  return (
    <section className="psy-test-builder">
      <header className="psy-test-builder__head">
        <div>
          <h1>Создание теста</h1>
          <p>Конструктор работает с API психолога: черновик можно сохранить, а затем опубликовать.</p>
        </div>
        <div className="psy-test-builder__actions">
          <Link className="dashboard__btn dashboard__btn--ghost" to="/dashboard/tests">
            ← К списку тестов
          </Link>
          <button type="button" className="dashboard__btn dashboard__btn--ghost" disabled={saving} onClick={() => onSave(false)}>
            {saving ? "Сохраняем..." : "Сохранить черновик"}
          </button>
          <button type="button" className="dashboard__btn dashboard__btn--main" disabled={saving} onClick={() => onSave(true)}>
            {saving ? "Публикуем..." : "Опубликовать"}
          </button>
        </div>
      </header>

      <div className="psy-test-builder__layout">
        <article className="psy-test-builder__card">
          <h2>Основные данные</h2>
          <label>
            <span>Название</span>
            <input value={form.title} onChange={updateField("title")} placeholder="Например: Шкала тревожности перед сессией" />
          </label>
          <label>
            <span>Тема</span>
            <input value={form.topic} onChange={updateField("topic")} placeholder="Тревожность, выгорание, отношения" />
          </label>
          <label>
            <span>Описание</span>
            <textarea rows={5} value={form.description} onChange={updateField("description")} placeholder="Кому подходит тест и как интерпретировать результат" />
          </label>
          <div className="psy-test-builder__grid2">
            <label>
              <span>Время прохождения, минут</span>
              <input type="number" min="1" max="240" value={form.estimatedMinutes} onChange={updateField("estimatedMinutes")} />
            </label>
            <label>
              <span>Доступ</span>
              <select value={form.scope} onChange={updateField("scope")}>
                <option value="PRIVATE">Только мои клиенты</option>
                <option value="PUBLIC">Публичный</option>
              </select>
            </label>
          </div>
        </article>

        <article className="psy-test-builder__card">
          <div className="psy-test-builder__section-head">
            <h2>Вопросы</h2>
            <button type="button" className="dashboard__btn dashboard__btn--ghost" onClick={addQuestion}>+ Вопрос</button>
          </div>
          {form.questions.map((question, index) => (
            <div className="psy-test-builder__question" key={`q-${index}`}>
              <div className="psy-test-builder__question-head">
                <strong>Вопрос {index + 1}</strong>
                <button type="button" className="dashboard__btn dashboard__btn--ghost" onClick={() => removeQuestion(index)}>
                  Удалить
                </button>
              </div>
              <label>
                <span>Текст вопроса</span>
                <input value={question.text} onChange={(e) => updateQuestion(index, { text: e.target.value })} placeholder="Например: Как часто вы испытывали напряжение за последнюю неделю?" />
              </label>
              <div className="psy-test-builder__grid2">
                <label>
                  <span>Тип</span>
                  <select value={question.type} onChange={(e) => updateQuestion(index, { type: e.target.value })}>
                    <option value="SCALE">Шкала 1–10</option>
                    <option value="SINGLE">Один вариант</option>
                    <option value="MULTIPLE">Несколько вариантов</option>
                    <option value="TEXT">Текстовый ответ</option>
                  </select>
                </label>
                <label className="psy-test-builder__check">
                  <input type="checkbox" checked={question.required !== false} onChange={(e) => updateQuestion(index, { required: e.target.checked })} />
                  <span>Обязательный</span>
                </label>
              </div>
              {(question.type === "SINGLE" || question.type === "MULTIPLE") ? (
                <label>
                  <span>Варианты, каждый с новой строки</span>
                  <textarea rows={4} value={question.options} onChange={(e) => updateQuestion(index, { options: e.target.value })} placeholder={"Да\nНет\nЗатрудняюсь ответить"} />
                </label>
              ) : null}
            </div>
          ))}
        </article>
      </div>
      {limitModal}
    </section>
  );
}
